import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiFetch } from "../data/apiFetch";
import { ALL_SUBJECT_KEYS, SUBJECT_GROUP_ORDER, SUBJECTS_BY_GROUP } from "../lib/subjectCatalog";
import { DEGREE_KEYS, TITLE_KEYS } from "../lib/academic";
import CredentialVerification from "./components/CredentialVerification";
import ServicePackagesEditor from "./components/ServicePackagesEditor";

const LS_KEY = "specialistProfileDraft:v2";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "http://localhost:3001";

const absUrl = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  return `${API_BASE}${u.startsWith("/") ? "" : "/"}${u}`;
};

const initialState = {
  displayName: "",
  city: "",
  avatarUrl: "",
  bio: "",
  subjects: [],
  levels: [],
  languages: [],
  rate: "",
  gender: "",
  birthYear: "",
  trialIsFree: false,
  trialPrice: "",
  workArea: "",
  sphere: "",
  degree: "",
  academicTitle: "",
  experienceYears: "",
};

function safeInt(raw) {
  const n = Number(String(raw ?? "").replace(",", "."));
  if (!Number.isFinite(n)) return null;
  return Math.trunc(n);
}

function clampYear(y) {
  const year = safeInt(y);
  if (!year) return "";
  const max = new Date().getFullYear() - 12;
  const min = 1940;
  return String(Math.min(Math.max(year, min), max));
}

async function loadMyProfile() {
  const candidates = ["/api/me/profile", "/api/tutors/me", "/api/profile/me"];
  let lastErr = null;
  for (const path of candidates) {
    try {
      return await apiFetch(path, { method: "GET" });
    } catch (e) {
      lastErr = e;
      if (e?.status === 404) continue;
      throw e;
    }
  }
  if (lastErr?.status === 404) return null;
  throw lastErr || new Error("Failed to load profile");
}

async function saveMyProfile(payload) {
  const candidates = ["/api/me/profile", "/api/tutors/me", "/api/profile/me"];
  let lastErr = null;
  for (const path of candidates) {
    try {
      return await apiFetch(path, { method: "PUT", body: JSON.stringify(payload) });
    } catch (e) {
      lastErr = e;
      if (e?.status === 404) continue;
      throw e;
    }
  }
  throw lastErr || new Error("Failed to save profile");
}

async function loadMyVerification() {
  return apiFetch("/api/verification/me", { method: "GET" });
}

async function startMyVerification() {
  return apiFetch("/api/verification/start", { method: "POST" });
}

async function generateBioWithAI({ displayName, subject, specializations, levels, city, rate, languages, lng }) {
  // apiFetch obtains a FRESH Firebase ID token for every call. The previous
  // implementation reused the (long-expired after 1h) token from
  // localStorage, so bio generation silently failed with 401 for any
  // session older than an hour.
  const data = await apiFetch("/api/ai/generate-bio", {
    method: "POST",
    body: JSON.stringify({ displayName, subject, specializations, levels, city, rate, languages, lng }),
  });
  if (!data?.bio) throw new Error("Empty response from server");
  return data.bio;
}

// ─── Dynamic Recommendation ───────────────────────────────────────────────────
function getDynamicRecommendation(profileChecks, profileScore, bioLength, t) {
  // Step 1: personal info (name + photo together)
  if (!profileChecks.hasName || !profileChecks.hasPhoto) return t("tutorSetup.rec.fillPersonal");
  // Step 2: specialization
  if (!profileChecks.hasMainSubject) return t("tutorSetup.rec.addSubject");
  // Step 3: experience / bio
  if (!profileChecks.hasBio && bioLength === 0) return t("tutorSetup.rec.writeBio");
  if (!profileChecks.hasBio && bioLength > 0) return t("tutorSetup.rec.expandBio");
  // Step 4: trial consultation
  if (!profileChecks.hasTrial) return t("tutorSetup.rec.addTrial");
  // Step 5: hourly rate
  if (!profileChecks.hasPrice) return t("tutorSetup.rec.addPrice");
  // Step 5: levels & languages
  if (!profileChecks.hasLevels) return t("tutorSetup.rec.addLevels");
  if (!profileChecks.hasLanguages) return t("tutorSetup.rec.addLanguages");
  // Step 6: verification
  if (!profileChecks.isVerified) return t("tutorSetup.rec.getVerified");
  return t("tutorSetup.rec.allDone");
}

// ─── Pricing Hint ─────────────────────────────────────────────────────────────
function getPricingHint(rate, t, isUk) {
  const r = safeInt(rate);
  if (!r) return t("tutorSetup.pricing.empty");
  // UAH thresholds for Ukrainian locale, EUR thresholds for English
  if (isUk) {
    if (r < 200) return t("tutorSetup.pricing.low");
    if (r <= 500) return t("tutorSetup.pricing.good");
    if (r <= 900) return t("tutorSetup.pricing.high");
  } else {
    if (r < 10) return t("tutorSetup.pricing.low");
    if (r <= 30) return t("tutorSetup.pricing.good");
    if (r <= 60) return t("tutorSetup.pricing.high");
  }
  return t("tutorSetup.pricing.premium");
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function Field({ label, hint, children, required = false, t }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2">
        <div className="text-sm text-white/85">{label}</div>
        {required ? (
          <span className="rounded-full bg-[#7269e3]/20 px-2 py-0.5 text-[10px] font-medium text-[#d8d4ff] ring-1 ring-[#7269e3]/30">
            {t("common.required")}
          </span>
        ) : null}
      </div>
      {children}
      {hint ? <div className="mt-1.5 text-xs text-white/55">{hint}</div> : null}
    </div>
  );
}

function TagPicker({ title, hint, value, onToggle, options, renderLabel }) {
  const selected = Array.isArray(value) ? value : [];
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <div className="font-semibold text-white">{title}</div>
        {hint ? <div className="text-xs text-white/55">{hint}</div> : null}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = selected.includes(opt);
          return (
            <button
              type="button"
              key={opt}
              onClick={() => onToggle(opt)}
              className={`rounded-xl px-3 py-1.5 text-sm ring-1 transition ${
                active
                  ? "bg-[#7269e3]/25 ring-[#7269e3]/40 text-white"
                  : "bg-white/5 ring-white/10 text-white/80 hover:bg-white/10"
              }`}
            >
              {renderLabel ? renderLabel(opt) : opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CheckItem({ done, children, strong = false }) {
  return (
    <div className={`flex items-start gap-3 rounded-xl px-3 py-2 ${done ? "bg-emerald-500/8" : "bg-white/5"}`}>
      <div
        className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold ${
          done
            ? "bg-emerald-400/20 text-emerald-300 ring-1 ring-emerald-300/20"
            : "bg-white/10 text-white/60 ring-1 ring-white/10"
        }`}
      >
        {done ? "✓" : "•"}
      </div>
      <div className={strong ? "text-white" : "text-white/80"}>{children}</div>
    </div>
  );
}

function ScorePill({ score, ready, t }) {
  const tone =
    score >= 80
      ? "bg-emerald-500/15 text-emerald-300 ring-emerald-400/20"
      : score >= 50
      ? "bg-amber-400/15 text-amber-200 ring-amber-300/20"
      : "bg-white/10 text-white/75 ring-white/10";
  return (
    <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm ring-1 ${tone}`}>
      <span className="font-semibold">{score}/100</span>
      <span className="text-white/75">
        {ready ? t("tutorSetup.status.ready") : t("tutorSetup.status.draft")}
      </span>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function TutorProfileSetup() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [generatingBio, setGeneratingBio] = useState(false);
  const fileInputRef = useRef(null);

  const auth = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("auth") || "null");
    } catch {
      return null;
    }
  }, []);

  const lng = i18n.resolvedLanguage || i18n.language || "en";
  const isUk = lng.startsWith("uk");
  const currency = isUk ? "₴" : "€";

  // Keys are already the locale keys — no toId needed
  const subjectLabel = useCallback(
    (s) => t(`subjects.${s}`, { defaultValue: s }),
    [t]
  );

  const [customLangInput, setCustomLangInput] = useState("");
  const [customSubjectInput, setCustomSubjectInput] = useState("");
  const [sphereQuery, setSphereQuery] = useState("");
  const [showSphereSug, setShowSphereSug] = useState(false);

  const [form, setForm] = useState(initialState);

  // Subject options for the chosen sphere
  // NOTE: must be declared AFTER form state to avoid temporal dead zone
  const SUBJECT_OPTIONS = useMemo(
    () => form.sphere && SUBJECTS_BY_GROUP[form.sphere]
      ? SUBJECTS_BY_GROUP[form.sphere]
      : [],
    [form.sphere]
  );
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");

  const [verification, setVerification] = useState({
    isVerified: false,
    verificationStatus: "NOT_STARTED",
    verificationMethod: null,
    verifiedAt: null,
    telegramLinked: false,
  });
  const [verificationLoading, setVerificationLoading] = useState(true);
  const [startingVerification, setStartingVerification] = useState(false);

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(""), 3500);
    return () => clearTimeout(id);
  }, [toast]);

  const uploadAvatar = async (file) => {
    if (!file) return;
    setUploadingAvatar(true);
    setToast("");
    try {
      const fd = new FormData();
      fd.append("avatar", file);
      const res = await fetch(`${API_BASE}/api/me/avatar`, {
        method: "POST",
        headers: { Authorization: `Bearer ${auth?.token}` },
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || data?.message || "Upload failed");
      setForm((f) => ({ ...f, avatarUrl: data.avatarUrl || "" }));
      setToast(t("tutorSetup.toasts.avatarUploaded"));
    } catch (e) {
      setToast(e?.message || t("tutorSetup.toasts.avatarUploadFailed"));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleGenerateBio = async () => {
  setGeneratingBio(true);
  setToast("");
  try {
    const bio = await generateBioWithAI({
      displayName: form.displayName,
      // Send human-readable labels, not raw catalog keys — the model used to
      // receive "economics" instead of "Економіка" and no specializations at
      // all, which produced generic one-size-fits-all bios.
      subject: form.workArea
        ? t(`subjectGroups.${form.workArea}`, { defaultValue: form.workArea })
        : "",
      specializations: (form.subjects || []).map((s) =>
        t(`subjects.${s}`, { defaultValue: s })
      ),
      levels: form.levels,
      city: form.city,
      rate: form.rate,
      languages: form.languages,
      lng,
    });
    setForm((f) => ({ ...f, bio }));
    setToast(t("tutorSetup.toasts.bioGenerated"));
  } catch (e) {
    setToast(e?.message || t("tutorSetup.toasts.bioGenerateFailed"));
  } finally {
    setGeneratingBio(false);
  }
};

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const draft = JSON.parse(raw);
        setForm((f) => ({ ...f, ...draft }));
      }
    } catch {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(form));
    } catch {}
  }, [form]);

  const load = useCallback(async () => {
    if (!auth?.token) { setLoading(false); return; }
    setLoading(true);
    setToast("");
    try {
      const me = await loadMyProfile();
      if (!me) { setLoading(false); return; }
      const p = me.profile ?? me;
      const loadedSubjects = Array.isArray(p.subjects) ? p.subjects : [];
      setForm((f) => ({
        ...f,
        displayName: p.displayName ?? p.name ?? f.displayName ?? "",
        city: p.city ?? f.city ?? "",
        avatarUrl: p.avatarUrl ?? f.avatarUrl ?? "",
        bio: p.bio ?? p.about ?? f.bio ?? "",
        subjects: loadedSubjects.length ? loadedSubjects : f.subjects,
        levels: Array.isArray(p.levels) ? p.levels : f.levels,
        languages: Array.isArray(p.languages) ? p.languages : f.languages,
        rate: typeof p.rate === "number" ? String(p.rate) : f.rate,
        gender: typeof p.gender === "string" ? p.gender : f.gender,
        birthYear: typeof p.birthYear === "number" ? String(p.birthYear) : f.birthYear,
        trialIsFree: !!p.trialIsFree,
        trialPrice: typeof p.trialPrice === "number" ? String(p.trialPrice) : f.trialPrice,
        sphere: typeof p.sphere === "string" ? p.sphere : f.sphere,
        degree: typeof p.degree === "string" ? p.degree : f.degree,
        academicTitle: typeof p.academicTitle === "string" ? p.academicTitle : f.academicTitle,
        experienceYears: typeof p.experienceYears === "number" ? String(p.experienceYears) : f.experienceYears,
      }));
      if (typeof p.sphere === "string" && p.sphere) {
        const sphereKey = p.sphere;
        setSphereQuery(SUBJECTS_BY_GROUP[sphereKey] ? t(`subjectGroups.${sphereKey}`) : sphereKey);
      }
    } catch (e) {
      console.warn("Profile load failed:", e?.message || e);
    } finally {
      setLoading(false);
    }
  }, [auth?.token, t]);

  const loadVerification = useCallback(async () => {
    if (!auth?.token) { setVerificationLoading(false); return; }
    setVerificationLoading(true);
    try {
      const data = await loadMyVerification();
      setVerification({
        isVerified: !!data?.isVerified,
        verificationStatus: data?.verificationStatus || "NOT_STARTED",
        verificationMethod: data?.verificationMethod || null,
        verifiedAt: data?.verifiedAt || null,
        telegramLinked: !!data?.telegramLinked,
      });
    } catch (e) {
      console.warn("Verification load failed:", e?.message || e);
    } finally {
      setVerificationLoading(false);
    }
  }, [auth?.token]);

  useEffect(() => {
    const onFocus = () => loadVerification();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadVerification]);

  useEffect(() => {
    load();
    loadVerification();
  }, [load, loadVerification]);

  const toggleInArray = (key, value) => {
    setForm((f) => {
      const cur = Array.isArray(f[key]) ? f[key] : [];
      const has = cur.includes(value);
      return { ...f, [key]: has ? cur.filter((x) => x !== value) : [...cur, value] };
    });
  };

  const handleStartVerification = async () => {
    if (!auth?.token) { navigate("/login"); return; }
    setStartingVerification(true);
    setToast("");
    try {
      const data = await startMyVerification();
      if (data?.deepLink) { window.location.href = data.deepLink; return; }
      await loadVerification();
    } catch (e) {
      setToast(e?.message || t("tutorSetup.toasts.verificationStartFailed"));
    } finally {
      setStartingVerification(false);
    }
  };

  // Sphere combobox suggestions
  const sphereSuggestions = useMemo(() => {
    const q = String(sphereQuery || "").trim().toLowerCase();
    if (!q) return SUBJECT_GROUP_ORDER.map((gid) => ({ value: gid, label: t(`subjectGroups.${gid}`) }));
    return SUBJECT_GROUP_ORDER
      .map((gid) => {
        const label = t(`subjectGroups.${gid}`);
        const hay = `${gid} ${label}`.toLowerCase();
        const score = hay.startsWith(q) ? 3 : hay.includes(q) ? 1 : 0;
        return { value: gid, label, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score || a.label.localeCompare(b.label));
  }, [sphereQuery, t]);

  // First selected subject label for preview
  const firstSubjectLabel = useMemo(() => {
    const first = (form.subjects || [])[0];
    if (!first) return null;
    return ALL_SUBJECT_KEYS.includes(first) ? subjectLabel(first) : first;
  }, [form.subjects, subjectLabel]);

  const addCustomSubject = useCallback(() => {
    const val = customSubjectInput.trim();
    if (!val || (form.subjects || []).includes(val)) { setCustomSubjectInput(""); return; }
    setForm(f => ({ ...f, subjects: [...(f.subjects || []), val] }));
    setCustomSubjectInput("");
  }, [customSubjectInput, form.subjects]);

  const bioLength = String(form.bio || "").trim().length;

  // ─── Score: max 90 without verification (+10 with) ────────────────────────
  const profileChecks = useMemo(() => ({
    hasName: !!String(form.displayName || "").trim(),
    hasPhoto: !!String(form.avatarUrl || "").trim(),
    hasMainSubject: Array.isArray(form.subjects) && form.subjects.length > 0,
    hasBio: bioLength >= 80,
    hasTrial:
      !!form.trialIsFree ||
      (Number.isFinite(safeInt(form.trialPrice)) && safeInt(form.trialPrice) > 0),
    hasPrice: Number.isFinite(safeInt(form.rate)) && safeInt(form.rate) > 0,
    hasLevels: Array.isArray(form.levels) && form.levels.length > 0,
    hasLanguages: Array.isArray(form.languages) && form.languages.length > 0,
    hasCity: !!String(form.city || "").trim(),
    isVerified: verification.verificationStatus === "APPROVED",
  }), [form, bioLength, verification.verificationStatus]);

  const profileScore = useMemo(() => {
    let score = 0;
    if (profileChecks.hasName)        score += 10;
    if (profileChecks.hasPhoto)       score += 15;
    if (profileChecks.hasMainSubject) score += 20;
    if (profileChecks.hasBio)         score += 15;
    if (profileChecks.hasPrice)       score += 10;
    if (profileChecks.hasTrial)       score += 10;
    if (profileChecks.hasLevels)      score += 5;
    if (profileChecks.hasLanguages)   score += 5;
    if (profileChecks.isVerified)     score += 10;
    return Math.min(score, 100);
  }, [profileChecks]);

  const isProfileReady = profileScore >= 70;

  const profileStage = useMemo(() => {
    if (profileScore >= 85) return "strong";
    if (profileScore >= 70) return "ready";
    if (profileScore >= 40) return "growing";
    return "draft";
  }, [profileScore]);

  const profileStageLabel = useMemo(() => {
    const map = { strong: "strong", ready: "ready", growing: "growing", draft: "draft" };
    return t(`tutorSetup.stage.${map[profileStage]}`);
  }, [profileStage, t]);

  // ─── Dynamic recommendation ───────────────────────────────────────────────
  const dynamicRec = useMemo(
    () => getDynamicRecommendation(profileChecks, profileScore, bioLength, t),
    [profileChecks, profileScore, bioLength, t]
  );

  // ─── Pricing hint ─────────────────────────────────────────────────────────
  const pricingHint = useMemo(() => getPricingHint(form.rate, t, isUk), [form.rate, t, isUk]);

  const bioStatus = useMemo(() => {
    if (bioLength >= 140) return { text: t("tutorSetup.bio.good"), tone: "text-emerald-300" };
    if (bioLength >= 80)  return { text: t("tutorSetup.bio.ok"),   tone: "text-white/75" };
    if (bioLength > 0)    return { text: t("tutorSetup.bio.short"), tone: "text-amber-200" };
    return { text: t("tutorSetup.bio.empty"), tone: "text-white/55" };
  }, [bioLength, t]);

  const onSave = async (e) => {
    e.preventDefault();
    if (!auth?.token) return navigate("/login");
    setSaving(true);
    setToast("");
    try {
      const rateNum = safeInt(form.rate);
      const birthYearNum = safeInt(form.birthYear);
      const trialPriceNum = safeInt(form.trialPrice);
      const trialIsFree = !!form.trialIsFree;
      const trialPrice =
        trialIsFree
          ? 0
          : Number.isFinite(trialPriceNum) && trialPriceNum > 0
          ? trialPriceNum
          : null;

      await saveMyProfile({
        displayName: String(form.displayName || "").trim() || null,
        city: String(form.city || "").trim() || null,
        avatarUrl: String(form.avatarUrl || "").trim() || null,
        bio: String(form.bio || "").trim() || null,
        subjects: Array.isArray(form.subjects) ? form.subjects : [],
        levels: Array.isArray(form.levels) ? form.levels : [],
        languages: Array.isArray(form.languages) ? form.languages : [],
        rate: Number.isFinite(rateNum) && rateNum > 0 ? rateNum : null,
        currency: isUk ? "UAH" : "EUR",
        language: isUk ? "uk" : "en",
        gender: form.gender ? String(form.gender).toLowerCase() : null,
        birthYear: Number.isFinite(birthYearNum) ? birthYearNum : null,
        trialIsFree,
        trialPrice,
        sphere: String(form.sphere || ""),
        degree: form.degree || null,
        academicTitle: form.academicTitle || null,
        experienceYears: safeInt(form.experienceYears) ?? null,
      });

      try { localStorage.removeItem(LS_KEY); } catch {}

      if (!isProfileReady) {
        setToast(t("tutorSetup.toasts.draftSaved"));
        return;
      }
      setToast(t("tutorSetup.toasts.saved"));
      navigate("/dashboard/tutor");
    } catch (e2) {
      setToast(e2?.message || t("tutorSetup.toasts.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (!auth?.token) {
    return (
      <main className="min-h-screen bg-[#272c39] text-white">
        <div className="mx-auto max-w-4xl px-4 py-14">
          <div className="rounded-2xl bg-white/5 p-8 ring-1 ring-white/10">
            <div className="text-white/80">{t("auth.needLogin")}</div>
            <button
              onClick={() => navigate("/login")}
              className="mt-4 rounded-xl bg-[#7269e3] px-4 py-2 font-semibold hover:brightness-110"
            >
              {t("actions.login")}
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#272c39] text-white">
      <div className="mx-auto max-w-6xl px-4 pb-24 pt-10">
        <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="mb-3">
              <ScorePill score={profileScore} ready={isProfileReady} t={t} />
            </div>
            <h1 className="font-unbounded text-3xl font-extrabold md:text-4xl">
              {t("tutorSetup.title")}
            </h1>
            <p className="mt-2 max-w-2xl text-white/78">{t("tutorSetup.subtitle")}</p>
          </div>
          <button
            onClick={() => navigate("/dashboard/tutor")}
            className="rounded-xl border border-white/20 px-4 py-2 hover:bg-white/5"
          >
            {t("actions.back")}
          </button>
        </header>

        {toast ? (
          <div className="mt-6 rounded-2xl bg-white/10 px-4 py-3 text-sm ring-1 ring-white/15">
            {toast}
          </div>
        ) : null}

        <section className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_360px]">
          {/* Score card */}
          <div className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="text-sm text-white/60">{t("tutorSetup.strength.title")}</div>
                <div className="mt-1 text-3xl font-extrabold">{profileScore} / 100</div>
                <div className="mt-2 text-sm text-white/72">{profileStageLabel}</div>

                {/* Verification nudge: show max possible without it */}
                {!profileChecks.isVerified && profileScore >= 60 && (
                  <div className="mt-2 text-xs text-[#d8d4ff]/80">
                    {t("tutorSetup.strength.verifNudge")}
                  </div>
                )}
              </div>

              <div className="w-full md:w-[48%]">
                <div className="h-2 overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full bg-[#7269e3] transition-all duration-500"
                    style={{ width: `${profileScore}%` }}
                  />
                </div>
                {/* max 90 label */}
                <div className="mt-1 text-right text-xs text-white/40">
                  {t("tutorSetup.strength.maxWithout")}
                </div>
              </div>
            </div>

            <div className="mt-5 grid gap-2 md:grid-cols-2">
              <CheckItem done={profileChecks.hasPhoto} strong>{t("tutorSetup.checks.photo")}</CheckItem>
              <CheckItem done={profileChecks.hasName} strong>{t("tutorSetup.checks.name")}</CheckItem>
              <CheckItem done={profileChecks.hasMainSubject} strong>{t("tutorSetup.checks.subject")}</CheckItem>
              <CheckItem done={profileChecks.hasBio} strong>{t("tutorSetup.checks.bio")}</CheckItem>
              <CheckItem done={profileChecks.hasTrial}>{t("tutorSetup.checks.trial")}</CheckItem>
              <CheckItem done={profileChecks.hasPrice}>{t("tutorSetup.checks.price")}</CheckItem>
              <CheckItem done={profileChecks.isVerified}>{t("tutorSetup.checks.verified")}</CheckItem>
            </div>

            {/* Dynamic recommendation */}
            <div className="mt-5 rounded-xl bg-[#7269e3]/10 p-4 ring-1 ring-[#7269e3]/20">
              <div className="text-xs font-semibold uppercase tracking-wide text-[#d8d4ff]/70 mb-1">
                {t("tutorSetup.rec.label")}
              </div>
              <div className="text-sm text-white/85">{dynamicRec}</div>
            </div>
          </div>

          {/* Preview aside */}
          <aside className="space-y-6">
            <div className="rounded-2xl bg-white/5 p-5 ring-1 ring-white/10">
              <div className="text-sm text-white/60">{t("tutorSetup.preview.title")}</div>
              <div className="mt-4 rounded-2xl bg-white/5 p-4 ring-1 ring-white/10">
                <div className="flex items-center gap-3">
                  {form.avatarUrl ? (
                    <img
                      crossOrigin="anonymous"
                      src={absUrl(form.avatarUrl)}
                      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.style.display = "none"; }}
                      alt=""
                      className="h-14 w-14 rounded-full object-cover ring-2 ring-white/15"
                    />
                  ) : (
                    <div className="h-14 w-14 shrink-0 rounded-full bg-white/10 ring-2 ring-white/15 flex items-center justify-center text-white/35">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
                        <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z" clipRule="evenodd"/>
                      </svg>
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-white">
                      {form.displayName || t("tutorSetup.preview.nameFallback")}
                    </div>
                    <div className="truncate text-sm text-white/60">
                      {firstSubjectLabel || t("tutorSetup.preview.subjectFallback")}
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {form.rate ? (
                    <span className="rounded-full bg-white/10 px-3 py-1 text-xs text-white/80 ring-1 ring-white/10">
                      {currency}{form.rate} / {t("tutorSetup.preview.hour")}
                    </span>
                  ) : null}
                  {form.trialIsFree ? (
                    <span className="rounded-full bg-emerald-500/15 px-3 py-1 text-xs text-emerald-300 ring-1 ring-emerald-400/20">
                      {t("tutorSetup.preview.freeTrial")}
                    </span>
                  ) : safeInt(form.trialPrice) ? (
                    <span className="rounded-full bg-white/10 px-3 py-1 text-xs text-white/80 ring-1 ring-white/10">
                      {t("tutorSetup.preview.trial")}: {form.trialPrice}
                    </span>
                  ) : null}
                  {verification.verificationStatus === "APPROVED" ? (
                    <span className="rounded-full bg-emerald-500/15 px-3 py-1 text-xs text-emerald-300 ring-1 ring-emerald-400/20">
                      {t("tutorSetup.preview.verified")}
                    </span>
                  ) : null}
                </div>
                <div className="mt-4 text-sm leading-6 text-white/72">
                  {form.bio?.trim() ? form.bio : t("tutorSetup.preview.bioFallback")}
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-white/5 p-5 ring-1 ring-white/10">
              <div className="font-semibold text-white">{t("tutorSetup.tips.title")}</div>
              <div className="mt-3 space-y-2 text-sm text-white/70">
                <div>{t("tutorSetup.tips.photo")}</div>
                <div>{t("tutorSetup.tips.bio")}</div>
                <div>{t("tutorSetup.tips.trial")}</div>
                <div>{t("tutorSetup.tips.verification")}</div>
              </div>
            </div>
          </aside>
        </section>

        <form onSubmit={onSave} className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_360px]">
          <div className="space-y-6">

            {/* Personal */}
            <section className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
              <div className="mb-5">
                <h2 className="font-unbounded text-xl font-semibold">{t("tutorSetup.sections.personal")}</h2>
                <p className="mt-2 text-sm text-white/65">{t("tutorSetup.sections.personalHint")}</p>
              </div>

              {loading ? (
                <div className="mt-4 text-white/70">{t("common.loading")}</div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Photo — spans both rows so name + rate stack on the right */}
                  <div className="md:row-span-2 md:pt-6">
                    <Field label={t("tutorSetup.fields.photo")} required hint={t("tutorSetup.hints.photo")} t={t}>
                      <div className="flex items-center gap-4">
                        {form.avatarUrl ? (
                          <img
                            crossOrigin="anonymous"
                            src={absUrl(form.avatarUrl)}
                            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.style.display = "none"; }}
                            alt=""
                            className="h-16 w-16 shrink-0 rounded-full object-cover ring-2 ring-white/20"
                          />
                        ) : (
                          <div className="h-16 w-16 shrink-0 rounded-full bg-white/10 ring-2 ring-white/20 flex items-center justify-center text-white/35">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                              <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z" clipRule="evenodd"/>
                            </svg>
                          </div>
                        )}
                        <div className="flex flex-col gap-2">
                          {/* Hidden native input — avoids browser-language "Choose file" text.
                              Triggered via a <label htmlFor>, not a button calling .click():
                              iOS Safari / some Android WebViews silently refuse to open the
                              native photo picker from a scripted .click() on a hidden input. */}
                          <input
                            ref={fileInputRef}
                            id="tutorAvatarFileInput"
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            disabled={uploadingAvatar || saving}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) uploadAvatar(file);
                              e.target.value = "";
                            }}
                            style={{ position: "absolute", width: 0, height: 0, opacity: 0, overflow: "hidden" }}
                          />
                          <label
                            htmlFor="tutorAvatarFileInput"
                            className={`w-max rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-sm text-white hover:bg-white/15 transition cursor-pointer ${
                              uploadingAvatar || saving ? "opacity-60 pointer-events-none" : ""
                            }`}
                          >
                            {uploadingAvatar ? "Завантаження…" : t("tutorSetup.actions.uploadPhoto")}
                          </label>
                          <div className="text-xs text-white/60">{t("tutorSetup.hints.photoHint")}</div>
                          {form.avatarUrl ? (
                            <button
                              type="button"
                              disabled={uploadingAvatar || saving}
                              onClick={() => { setForm((f) => ({ ...f, avatarUrl: "" })); setToast(t("tutorSetup.toasts.avatarRemoved")); }}
                              className="w-max rounded-xl border border-white/20 px-3 py-1.5 text-xs hover:bg-white/5 disabled:opacity-60"
                            >
                              {t("tutorSetup.actions.removePhoto")}
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </Field>
                  </div>

                  {/* Name — right column, row 1 */}
                  <Field label={t("tutorSetup.fields.fullName")} required t={t}>
                    <input
                      value={form.displayName}
                      onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                      className="w-full rounded-xl border border-white/10 bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#7269e3]"
                      placeholder={t("tutorSetup.placeholders.fullName")}
                    />
                  </Field>

                  {/* Rate — right column, row 2 (under name) */}
                  <Field
                    label={t("tutorSetup.fields.rateLabel")}
                    required
                    hint={pricingHint}
                    t={t}
                  >
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      value={form.rate}
                      onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))}
                      className="w-full rounded-xl border border-white/10 bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#7269e3]"
                      placeholder={t("tutorSetup.placeholders.rate")}
                    />
                  </Field>
                </div>
              )}

              {/* Gender & birth year — moved from Filters */}
              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <Field label={t("tutorSetup.filters.gender")} hint={t("tutorSetup.filters.genderHint")} t={t}>
                  <select
                    value={form.gender}
                    onChange={(e) => setForm((f) => ({ ...f, gender: e.target.value }))}
                    className="w-full rounded-xl border border-white/10 bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#7269e3]"
                  >
                    <option value="">{t("common.notSpecified")}</option>
                    <option value="female">{t("tutorSetup.filters.genderFemale")}</option>
                    <option value="male">{t("tutorSetup.filters.genderMale")}</option>
                    <option value="other">{t("tutorSetup.filters.genderOther")}</option>
                  </select>
                </Field>
                <Field label={t("tutorSetup.filters.birthYear")} hint={t("tutorSetup.filters.birthYearHint")} t={t}>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    value={form.birthYear}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
                      setForm((f) => ({ ...f, birthYear: digits }));
                    }}
                    onBlur={() => setForm((f) => ({ ...f, birthYear: clampYear(f.birthYear) }))}
                    className="w-full rounded-xl border border-white/10 bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#7269e3]"
                    placeholder={t("tutorSetup.filters.birthYearPh")}
                  />
                </Field>
              </div>

            </section>

            {/* Expertise */}
            <section className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
              <div className="mb-5">
                <h2 className="font-unbounded text-xl font-semibold">{t("tutorSetup.expertise.title")}</h2>
                <p className="mt-2 text-sm text-white/65">{t("tutorSetup.expertise.sectionHint")}</p>
              </div>

              {/* Step 1: Main sphere — free-text combobox */}
              <Field label={t("tutorSetup.expertise.sphere")} required hint={t("tutorSetup.expertise.sphereHint")} t={t}>
                <div className="relative">
                  <input
                    value={sphereQuery}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setSphereQuery(raw);
                      setShowSphereSug(true);
                      // store raw text as sphere (key resolution happens on pick)
                      setForm((f) => ({ ...f, sphere: raw, subjects: [] }));
                    }}
                    onFocus={() => setShowSphereSug(true)}
                    onBlur={() => setTimeout(() => setShowSphereSug(false), 120)}
                    className="w-full rounded-xl border border-white/10 bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#7269e3]"
                    placeholder={t("tutorSetup.expertise.spherePh")}
                  />
                  {showSphereSug && sphereSuggestions.length > 0 && (
                    <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-xl bg-[#1f2430] ring-1 ring-white/15 max-h-56 overflow-y-auto">
                      {sphereSuggestions.map((sug) => (
                        <button
                          key={sug.value}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setSphereQuery(sug.label);
                            setForm((f) => ({ ...f, sphere: sug.value, subjects: [] }));
                            setShowSphereSug(false);
                          }}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-white/10"
                        >
                          {sug.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </Field>

              {/* Step 2: Multi-select specializations */}
              {form.sphere && SUBJECT_OPTIONS.length > 0 ? (
                <Field label={t("tutorSetup.expertise.subjects")} required hint={t("tutorSetup.expertise.subjectsHint")} t={t}>
                  <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {SUBJECT_OPTIONS.map((key) => {
                      const checked = (form.subjects || []).includes(key);
                      return (
                        <label
                          key={key}
                          className={`flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 ring-1 transition ${
                            checked
                              ? "bg-[#7269e3]/20 ring-[#7269e3]/40"
                              : "bg-white/5 ring-white/10 hover:bg-white/10"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleInArray("subjects", key)}
                            className="h-4 w-4 accent-[#7269e3] shrink-0"
                          />
                          <span className={`text-sm ${checked ? "text-white" : "text-white/80"}`}>
                            {subjectLabel(key)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {/* Custom subject input */}
                  <div className="mt-4 border-t border-white/10 pt-4">
                    <div className="text-xs text-white/55 mb-2">
                      {isUk ? "Не знайшли потрібний напрям? Додайте свій:" : "Don't see what you need? Add your own:"}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={customSubjectInput}
                        onChange={(e) => setCustomSubjectInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomSubject(); } }}
                        placeholder={isUk ? "Напишіть свій напрям…" : "Type your specialization…"}
                        className="flex-1 rounded-xl border border-white/15 bg-white/5 px-3 py-1.5 text-sm text-white placeholder:text-white/35 focus:border-[#7269e3]/60 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={addCustomSubject}
                        disabled={!customSubjectInput.trim()}
                        className="rounded-xl bg-[#7269e3]/20 px-4 py-1.5 text-sm text-white ring-1 ring-[#7269e3]/40 hover:bg-[#7269e3]/30 transition disabled:opacity-40"
                      >
                        {isUk ? "Додати" : "Add"}
                      </button>
                    </div>
                    {(form.subjects || []).filter(s => !ALL_SUBJECT_KEYS.includes(s)).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {(form.subjects || []).filter(s => !ALL_SUBJECT_KEYS.includes(s)).map((s) => (
                          <span key={s} className="inline-flex items-center gap-1 rounded-xl bg-[#7269e3]/25 ring-1 ring-[#7269e3]/40 px-3 py-1.5 text-sm text-white">
                            {s}
                            <button
                              type="button"
                              onClick={() => setForm(f => ({ ...f, subjects: f.subjects.filter(x => x !== s) }))}
                              className="ml-0.5 text-white/50 hover:text-white transition"
                            >×</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {(form.subjects || []).length > 0 && (
                    <div className="mt-2 text-xs text-[#d8d4ff]/70">
                      {isUk
                        ? `Обрано: ${form.subjects.length}`
                        : `Selected: ${form.subjects.length}`}
                    </div>
                  )}
                </Field>
              ) : form.sphere ? (
                /* Custom sphere typed manually — show only custom input */
                <div className="mt-1">
                  <div className="text-sm text-white/85 mb-2">{t("tutorSetup.expertise.subjects")}</div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={customSubjectInput}
                      onChange={(e) => setCustomSubjectInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomSubject(); } }}
                      placeholder={isUk ? "Напишіть напрям…" : "Type a specialization…"}
                      className="flex-1 rounded-xl border border-white/15 bg-white/5 px-3 py-1.5 text-sm text-white placeholder:text-white/35 focus:border-[#7269e3]/60 focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={addCustomSubject}
                      disabled={!customSubjectInput.trim()}
                      className="rounded-xl bg-[#7269e3]/20 px-4 py-1.5 text-sm text-white ring-1 ring-[#7269e3]/40 hover:bg-[#7269e3]/30 transition disabled:opacity-40"
                    >
                      {isUk ? "Додати" : "Add"}
                    </button>
                  </div>
                  {(form.subjects || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(form.subjects || []).map((s) => (
                        <span key={s} className="inline-flex items-center gap-1 rounded-xl bg-[#7269e3]/25 ring-1 ring-[#7269e3]/40 px-3 py-1.5 text-sm text-white">
                          {ALL_SUBJECT_KEYS.includes(s) ? subjectLabel(s) : s}
                          <button
                            type="button"
                            onClick={() => setForm(f => ({ ...f, subjects: f.subjects.filter(x => x !== s) }))}
                            className="ml-0.5 text-white/50 hover:text-white transition"
                          >×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-1 rounded-xl bg-white/5 px-3 py-2.5 text-sm text-white/45 ring-1 ring-white/10">
                  {isUk ? "Спочатку оберіть сферу вище" : "Select a sphere above first"}
                </div>
              )}

              <div className="mt-6 space-y-5">
                <TagPicker
                  title={t("tutorSetup.fields.levels")}
                  hint={t("tutorSetup.hints.levels")}
                  value={form.levels}
                  onToggle={(v) => toggleInArray("levels", v)}
                  options={[
                    "beginner", "intermediate", "advanced", "professional",
                    "academic", "self_development", "corporate", "career_change",
                  ]}
                  renderLabel={(s) => t(`levels.${s}`, { defaultValue: s })}
                />
                {/* Language picker with custom language input */}
                {(() => {
                  const PRESET_LANGS = [
                    "ukrainian", "english", "russian", "polish",
                    "german", "french", "spanish", "italian",
                    "chinese", "czech", "arabic", "turkish", "hebrew",
                  ];
                  const customLangs = (form.languages || []).filter(l => !PRESET_LANGS.includes(l));
                  const addCustomLang = () => {
                    const val = customLangInput.trim();
                    if (!val || (form.languages || []).includes(val)) { setCustomLangInput(""); return; }
                    setForm(f => ({ ...f, languages: [...(f.languages || []), val] }));
                    setCustomLangInput("");
                  };
                  return (
                    <div>
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="font-semibold text-white">{t("tutorSetup.fields.languages")}</div>
                        <div className="text-xs text-white/55">{t("tutorSetup.hints.languages")}</div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {PRESET_LANGS.map((opt) => {
                          const active = (form.languages || []).includes(opt);
                          return (
                            <button
                              type="button"
                              key={opt}
                              onClick={() => toggleInArray("languages", opt)}
                              className={`rounded-xl px-3 py-1.5 text-sm ring-1 transition ${active ? "bg-[#7269e3]/25 ring-[#7269e3]/40 text-white" : "bg-white/5 ring-white/10 text-white/80 hover:bg-white/10"}`}
                            >
                              {t(`languages.${opt}`, { defaultValue: opt })}
                            </button>
                          );
                        })}
                        {/* Custom language chips */}
                        {customLangs.map((lang) => (
                          <span key={lang} className="inline-flex items-center gap-1 rounded-xl bg-[#7269e3]/25 ring-1 ring-[#7269e3]/40 px-3 py-1.5 text-sm text-white">
                            {lang}
                            <button
                              type="button"
                              onClick={() => setForm(f => ({ ...f, languages: f.languages.filter(l => l !== lang) }))}
                              className="ml-0.5 text-white/50 hover:text-white transition"
                            >×</button>
                          </span>
                        ))}
                      </div>
                      {/* Add custom language */}
                      <div className="mt-3 flex gap-2">
                        <input
                          type="text"
                          value={customLangInput}
                          onChange={e => setCustomLangInput(e.target.value)}
                          onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addCustomLang())}
                          placeholder={isUk ? "Інша мова…" : "Other language…"}
                          className="flex-1 rounded-xl border border-white/15 bg-white/5 px-3 py-1.5 text-sm text-white placeholder:text-white/35 focus:border-[#7269e3]/60 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={addCustomLang}
                          disabled={!customLangInput.trim()}
                          className="rounded-xl bg-[#7269e3]/20 px-4 py-1.5 text-sm text-white ring-1 ring-[#7269e3]/40 hover:bg-[#7269e3]/30 transition disabled:opacity-40"
                        >
                          {isUk ? "Додати" : "Add"}
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </section>

            {/* Bio — placed after Expertise so AI generation has specialization context */}
            <section className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
              <div className="mb-5">
                <h2 className="font-unbounded text-xl font-semibold">{t("tutorSetup.sections.experience")}</h2>
                <p className="mt-2 text-sm text-white/65">{t("tutorSetup.sections.experienceHint")}</p>
              </div>

              <div className="mb-1.5 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="text-sm text-white/85">{t("tutorSetup.fields.about")}</div>
                  <span className="rounded-full bg-[#7269e3]/20 px-2 py-0.5 text-[10px] font-medium text-[#d8d4ff] ring-1 ring-[#7269e3]/30">
                    {t("common.required")}
                  </span>
                </div>

                {/* ✨ AI Generate button */}
                <button
                  type="button"
                  disabled={generatingBio || saving}
                  onClick={handleGenerateBio}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[#7269e3]/20 px-3 py-1.5 text-xs font-medium text-[#d8d4ff] ring-1 ring-[#7269e3]/30 hover:bg-[#7269e3]/30 disabled:opacity-60 transition"
                >
                  {generatingBio ? (
                    <>
                      <span className="animate-spin">⟳</span>
                      {t("tutorSetup.bio.generating")}
                    </>
                  ) : (
                    <>
                      ✨ {t("tutorSetup.bio.aiBtn")}
                    </>
                  )}
                </button>
              </div>

              <textarea
                rows={5}
                value={form.bio}
                onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                className="w-full resize-y rounded-xl border border-white/10 bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#7269e3]"
                placeholder={t("tutorSetup.placeholders.about")}
              />

              <div className="mt-2 flex items-center justify-between gap-3">
                <div className={`text-xs ${bioStatus.tone}`}>{bioStatus.text}</div>
                <div className="text-xs text-white/45">{bioLength} chars</div>
              </div>

              <div className="mt-2 text-xs text-white/45">{t("tutorSetup.hints.about")}</div>
            </section>

            {/* Academic credentials — degree, title, experience */}
            <section className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
              <h2 className="font-unbounded text-xl font-semibold">{t("academic.sectionTitle")}</h2>
              <p className="mt-2 text-sm text-white/65">{t("academic.sectionSubtitle")}</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                <div>
                  <label className="mb-1 block text-sm text-white/80">{t("academic.degreeLabel")}</label>
                  <select
                    value={form.degree}
                    onChange={(e) => setForm((f) => ({ ...f, degree: e.target.value }))}
                    className="w-full rounded-xl border border-white/10 bg-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#7269e3]"
                  >
                    <option value="">{t("academic.notSet")}</option>
                    {DEGREE_KEYS.map((k) => (
                      <option key={k} value={k}>{t(`academic.degree.${k}`)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm text-white/80">{t("academic.titleLabel")}</label>
                  <select
                    value={form.academicTitle}
                    onChange={(e) => setForm((f) => ({ ...f, academicTitle: e.target.value }))}
                    className="w-full rounded-xl border border-white/10 bg-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#7269e3]"
                  >
                    <option value="">{t("academic.notSet")}</option>
                    {TITLE_KEYS.map((k) => (
                      <option key={k} value={k}>{t(`academic.title.${k}`)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm text-white/80">{t("academic.experienceLabel")}</label>
                  <input
                    type="number"
                    min="0"
                    max="70"
                    value={form.experienceYears}
                    onChange={(e) => setForm((f) => ({ ...f, experienceYears: e.target.value }))}
                    placeholder={t("academic.experiencePh")}
                    className="w-full rounded-xl border border-white/10 bg-white/10 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#7269e3]"
                  />
                </div>
              </div>
            </section>

            {/* Diploma / academic-title verification */}
            <CredentialVerification degree={form.degree} academicTitle={form.academicTitle} />

            {/* Fixed-price service packages */}
            <ServicePackagesEditor sphere={form.sphere} rate={form.rate} />

            {/* Trial */}
            <section className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
              <div className="mb-5">
                <div className="flex items-center gap-2">
                  <h2 className="font-unbounded text-xl font-semibold">{t("tutorSetup.trial.title")}</h2>
                  <span className="rounded-full bg-[#7269e3]/20 px-2 py-0.5 text-[10px] font-medium text-[#d8d4ff] ring-1 ring-[#7269e3]/30">
                    {t("common.required")}
                  </span>
                </div>
                <p className="mt-2 text-sm text-white/65">{t("tutorSetup.trial.subtitle")}</p>
              </div>
              <div className="rounded-2xl bg-white/5 p-5 ring-1 ring-white/10">
                <label className="flex items-center gap-2 text-sm text-white/85">
                  <input
                    type="checkbox"
                    checked={!!form.trialIsFree}
                    onChange={(e) => setForm((f) => ({ ...f, trialIsFree: e.target.checked, trialPrice: e.target.checked ? "" : f.trialPrice }))}
                  />
                  {t("tutorSetup.trial.free")}
                </label>
                <div className="mt-4">
                  <Field
                    label={t("tutorSetup.trial.price")}
                    hint={form.trialIsFree ? t("tutorSetup.trial.freeHint") : t("tutorSetup.trial.hint")}
                    t={t}
                  >
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      disabled={!!form.trialIsFree}
                      value={form.trialPrice}
                      onChange={(e) => setForm((f) => ({ ...f, trialPrice: e.target.value }))}
                      className="w-full rounded-xl border border-white/10 bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#7269e3] disabled:opacity-50"
                      placeholder={t("tutorSetup.trial.pricePh")}
                    />
                  </Field>
                  {!profileChecks.hasPrice ? (
                    <div className="mt-3 rounded-xl bg-amber-500/10 px-3 py-2 text-xs text-amber-200 ring-1 ring-amber-400/20">
                      {t("tutorSetup.trial.warning")}
                    </div>
                  ) : null}
                </div>
              </div>
            </section>

            {/* Verification */}
            <section className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="font-unbounded text-xl font-semibold">{t("tutorSetup.verification.title")}</h2>
                  <p className="mt-2 text-sm text-white/75">{t("tutorSetup.verification.subtitle")}</p>

                  {/* Score nudge for verification */}
                  {!profileChecks.isVerified && (
                    <p className="mt-1 text-xs text-[#d8d4ff]/70">
                      {t("tutorSetup.verification.scoreNudge")}
                    </p>
                  )}
                </div>
                <div>
                  {verificationLoading ? (
                    <span className="inline-flex rounded-full bg-white/10 px-3 py-1 text-sm text-white/70 ring-1 ring-white/10">{t("common.loading")}</span>
                  ) : verification.verificationStatus === "APPROVED" ? (
                    <span className="inline-flex rounded-full bg-emerald-500/15 px-3 py-1 text-sm text-emerald-300 ring-1 ring-emerald-400/20">✅ {t("tutorSetup.verification.approved")}</span>
                  ) : verification.verificationStatus === "SUBMITTED" ? (
                    <span className="inline-flex rounded-full bg-amber-500/15 px-3 py-1 text-sm text-amber-200 ring-1 ring-amber-400/20">🕐 {t("tutorSetup.verification.submitted")}</span>
                  ) : verification.verificationStatus === "PENDING" ? (
                    <span className="inline-flex rounded-full bg-white/10 px-3 py-1 text-sm text-white/80 ring-1 ring-white/10">🕐 {t("tutorSetup.verification.pending")}</span>
                  ) : verification.verificationStatus === "REJECTED" ? (
                    <span className="inline-flex rounded-full bg-red-500/15 px-3 py-1 text-sm text-red-200 ring-1 ring-red-400/20">❌ {t("tutorSetup.verification.rejected")}</span>
                  ) : (
                    <span className="inline-flex rounded-full bg-white/10 px-3 py-1 text-sm text-white/70 ring-1 ring-white/10">{t("tutorSetup.verification.notStarted")}</span>
                  )}
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="text-sm text-white/70">
                  {verification.verificationStatus === "APPROVED" ? t("tutorSetup.verification.approvedHint")
                    : verification.verificationStatus === "SUBMITTED" ? t("tutorSetup.verification.submittedHint")
                    : verification.verificationStatus === "PENDING" ? t("tutorSetup.verification.pendingHint")
                    : verification.verificationStatus === "REJECTED" ? t("tutorSetup.verification.rejectedHint")
                    : t("tutorSetup.verification.notStartedHint")}
                </div>
                {(verification.verificationStatus === "NOT_STARTED" ||
                  verification.verificationStatus === "REJECTED" ||
                  verification.verificationStatus === "PENDING") && (
                  <button
                    type="button"
                    onClick={handleStartVerification}
                    disabled={startingVerification}
                    className="rounded-xl bg-[#7269e3] px-5 py-2.5 font-semibold hover:brightness-110 disabled:opacity-60"
                  >
                    {startingVerification
                      ? t("tutorSetup.verification.openingTelegram")
                      : verification.verificationStatus === "REJECTED"
                      ? t("tutorSetup.verification.retry")
                      : verification.verificationStatus === "PENDING"
                      ? t("tutorSetup.verification.continue")
                      : t("tutorSetup.verification.start")}
                  </button>
                )}
              </div>
            </section>

            {/* Save */}
            <div className="flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={saving}
                className="rounded-xl bg-[#7269e3] px-5 py-2.5 font-semibold hover:brightness-110 disabled:opacity-60"
              >
                {saving ? t("tutorSetup.actions.saving") : isProfileReady ? t("tutorSetup.actions.saveReady") : t("tutorSetup.actions.saveDraft")}
              </button>
              <button
                type="button"
                onClick={() => { load(); loadVerification(); }}
                disabled={saving || verificationLoading}
                className="rounded-xl border border-white/20 px-5 py-2.5 hover:bg-white/5 disabled:opacity-60"
              >
                {t("actions.refresh")}
              </button>
            </div>

            <p className="text-xs text-white/60">
              {isProfileReady ? t("tutorSetup.saveHintReady") : t("tutorSetup.saveHintDraft")}
            </p>
          </div>

          {/* Right aside */}
          <aside className="space-y-6">
            <div className="rounded-2xl bg-white/5 p-5 ring-1 ring-white/10">
              <div className="font-semibold text-white">{t("tutorSetup.retention.title")}</div>
              <div className="mt-3 space-y-3 text-sm text-white/70">
                <div>{t("tutorSetup.retention.opens")}</div>
                <div>{t("tutorSetup.retention.trust")}</div>
                <div>{t("tutorSetup.retention.requests")}</div>
              </div>
            </div>

            <div className="rounded-2xl bg-white/5 p-5 ring-1 ring-white/10">
              <div className="font-semibold text-white">{t("tutorSetup.level.title")}</div>
              <div className="mt-3 text-sm text-white/72">
                {profileScore >= 85 ? t("tutorSetup.level.top")
                  : profileScore >= 70 ? t("tutorSetup.level.ready")
                  : profileScore >= 40 ? t("tutorSetup.level.growing")
                  : t("tutorSetup.level.new")}
              </div>
              <div className="mt-3 text-sm text-white/55">{t("tutorSetup.level.hint")}</div>
            </div>
          </aside>
        </form>
      </div>
    </main>
  );
}
